import _ from 'lodash';
import WixSelector from '../wixSelector';
import * as core from '../targets/core';
import ga from '../privates/analytics';
import namespaces from '../namespaces';
import appsExtendedNamespaces from '../appsExtendedNamespaces';
import Emitter from '../Emitter';
import Storage from '../Storage';
import {onReady, onRender} from '../privates/readyManager';
import InternalAPIs from './internalAPIs';
import platformUtils from '../privates/platformUtils';

let storage;
const emitter = new Emitter();
platformUtils.setEmitter(emitter);
platformUtils.setWixSelector(new WixSelector());
const location = new namespaces.Location();
const window = new namespaces.Window();
const site = new namespaces.Site();
const __INTERNAL__ = new InternalAPIs();

const getStorage = (appId, storageValue) => {
    if (!storage) {
        storage = new Storage();
    }
    return storage.createScopedStorage(appId, storageValue);
};
/**
 * @namespace $w
 * @summary The `$w` namespace contains everything you need in order to work
 *  with your site's components. 
 * @description
 *  The `$w` namespace contains all of the UI elements, nodes, and
 *  events that make up your site. It also includes the [`$w()`]($w/$w),
 *  [`onReady()`]($w.html#onReady), and [`at()`]($w.html#at) functions.
 *
 *  The APIs in `$w` can only be used in front-end code.
 *
 *  You do not need to import `$w`.
 * 
 *  > **Note:** Most `$w` elements, nodes, and events are described here under Wix Editor Elements. Some `$w` 
 *  elements are described in context of their functionality. For example, the [`WixForms`](wix-crm.html#$w-wixforms) 
 *  element is described under the `wix-crm` namespace because using the API for the Wix Forms app is most relevant in the 
 *  context of site contacts and visitors. 
 */

/**
 * @function $w
 * @memberof $w
 * @syntax
 * function $w(selector: string): Element | Element[]
 * @summary Selects and returns elements from a page.
 * @description
 *  The `$w()` function selects single or multiple elements by ID or type.
 *
 *  To select by ID, pass a selector string with the hash symbol
 *  (`#`) followed by the ID of the item you want to select (e.g. `"#myElement"`).
 *  The function returns the selected element object.
 *
 *  To select by type, pass a selector string with the name of
 *  the type without the preceding `#` (e.g. `"Button"`). The function returns
 *  an array of the selected element objects. An array is returned even if one
 *  or no elements are selected.
 *
 *  To select using multiple selectors, pass a selector string with
 *  multiple selectors separated by commas. The selectors in the
 *  comma-separated string can be ID selectors, type selectors, or a mixture of
 *  the two. The function returns an array of the selected element objects.
 *  An array is returned even if one or no elements are selected. If two or more
 *  selectors select the same element, it's still returned only once in the array.
 *
 *   > **Note:** Most elements accessible for selection by `$w` have basic API properties and functions,
 *  like `id`, `type`, `show()`, `hide()`, and others. Use Corvid's autocomplete
 *  in the code panel to see which API functions and properties are available
 *  for each element.
 * @param {external:String} selector A selector or multiple comma-separated selectors.
 * @returns {$w.Element|$w.Element[]} A single selected element or an array of selected elements.
 * @snippet [$w-select_by_name.es6=Select an element using its ID]
 * @snippet [$w-select_by_type.es6=Select elements by type]
 * @snippet [$w-select_images.es6=Select all the images on the page]
 * @snippet [$w-select_combo.es6=Select elements using multiple selectors]
 * @snippet [$w-select_and_run.es6=Hide all the page's images]
 */

/**
 * @callback $w.ReadyHandler
 * @summary Function that runs when all page elements have finished loading.
 * @returns {Promise<void>|void}
 * @fulfill {void} When the `onReady` function has completed.
 */


/**
 * @function onReady
 * @syntax
   * function onReady(initFunction: ReadyHandler): void
   * callback ReadyHandler(): Promise<void>
 * @summary Sets the function that runs when all the page elements have finished loading.
 * @description
 *  Use the `onReady()` function for code you want to run before the user starts
 *  interacting with your page.
 *
 *  The `onReady()` function in the **Site** tab is called before the
 *  `onReady()` function in the **Page** tab.
 *
 *  The following code *should* be placed inside the `onReady()` event handler:
 *
 *  + Initialization of element properties: Example: Setting a text element's initial text value.
 *  + Function calls on elements to set their initial state: Example: Disabling a button.
 *  + Dynamic event handlers that you want bound when the page loads: Example: Setting an event handler to be called when the mouse pointer enters an element.
 *  + For SEO, return all content you want search bots to see. Search bots can see the results of any function that runs in `onReady()` and whose promise is resolved before the `onReady()` promise is resolved. For asynchronous functions, ensure the function's promise resolves first by returning it in `onReady()`. Example: Return data queries in `onReady()` if the queried content is populating page elements.
 * 
 *
 *  The following code should *not* be placed inside the `onReady()` event handler:
 *
 *  + Static event handlers that are wired using the Properties panel in the Editor are not placed inside the `onReady()` event handler.
 *  + `return` statements containing synchronous functions, especially if there is no impact on rendering (such as a query) and no need for SEO. Avoiding these `return` statements can improve performance.
 * 
 * 
 * 
 *  ### Preventing double "side effects"
 * 
 *  The `onReady()` event handler may be called twice during the page rendering
 *  process, once server-side and once client-side.
 * 
 *  Because `onReady()` code may be run twice, you need to be aware that if your
 *  `onReady()` code causes a side effect, such as inserting an item into a collection,
 *  that side effect might happen twice. To avoid a side effect from happening twice,
 *  use the [`wixWindow.rendering.env`](wix-window.rendering.html#env) to determine
 *  where your code is being executed.
 * @param {$w.ReadyHandler} initFunction The name of the function or the function expression to run when the page has finished loading.
 * @snippet [$w-onReady-anonymous-long.es6=Run code in an anonymous function when the page loads]
 * @snippet [$w-onReady-promise.es6=Wait for a Promise to resolve]
 *  This example demonstrates the technique of returning a Promise so that the
 *  page doesn't load until the Promise resolves. Here, we wait for the query to
 *  finish and populate a table before displaying the page to users.
 * @snippet [$w-onReady-full.es6=Where to place code]
 *  This example demonstrates which code should be placed inside the `onReady()`
 *  event handler.
 * @snippet [$w-onReady-env.es6=Use the Rendering API to avoid inserting an item twice]
 *  This example demonstrates how to use the `env` property of the Rendering API to
 *  make sure an item is inserted into a collection only once.
 * @see [$w( )]($w/$w)
 * @memberof $w
 */

/**
 * @function at
 * @syntax
 * function at(context: EventContext): Function
 * @summary Gets a selector function for a specific context.
 * @description
 *  The `at()` function returns a scoped selector where the scope is based on the
 *  context property. Usually, you will use `at()` in a event handler that handles
 *  events fired on an element contained in a repeater to get a [selector with
 *  repeated item scope]($w.Repeater.html#repeated-item-scope). The returned function
 *  selects the elements from the same repeater item where the event was fired.
 * @snippet [$w-at.es6=Select an element in a repeated item]
 *  In this example, we have a repeater where each item contains an image and
 *  a text element. When an image is clicked, the value of the text element in
 *  the same repeated item is changed to "Selected". All the other text elements
 *  in the other repeated items are not affected.
 * @param {$w.Event~EventContext} context An event context.
 * @returns {$w.$w} A selector function for the given context.
 * @memberof $w
 */

const getSelector = (controllerId, logName) => {
    const $w = platformUtils.getWixSelector().scoped$W(controllerId, logName);
    _.assign($w, emitter.createScopedEmitter(controllerId));
    $w.onReady = function (callback) {
        onReady(callback, logName);
    };
    $w.onRender = function (callback) {
        onRender(callback, logName);
    };
    return $w;
};

const getScopedGlobalApis = (appDefId) => {
    const apis = {
        location: new appsExtendedNamespaces.Location(appDefId),
        window: new appsExtendedNamespaces.Window(appDefId),
        site: new appsExtendedNamespaces.Site(appDefId),
        events: new appsExtendedNamespaces.Events(appDefId)
    };

    _.forEach(core.moduleStore.getNamespaces(), (namespace) => {
        const scopedNamespace = core.moduleStore.get(namespace).scopedNamespace;
        if (scopedNamespace) {
            const Module = core.moduleStore.get(namespace).Module;
            apis[scopedNamespace] = new Module(appDefId);
        }
    });

    return apis;
};

self['wix-location'] = location;
self['wix-window'] = window;
self['wix-site'] = site;

export {
    site,
    location,
    window,
    __INTERNAL__,
    core,
    getSelector,
    getStorage,
    emitter,
    onReady,
    ga,
    getScopedGlobalApis
};
